import { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { CanonicalEntityData } from '@/services/api';

export interface ConceptTypeNode extends CanonicalEntityData {
  children: ConceptTypeNode[];
  depth: number;
}

interface ConceptTypeTreeProps {
  roots: ConceptTypeNode[];
  selectedId: string | null;
  onSelect: (node: ConceptTypeNode) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  physical_attribute: 'bg-sky-100 text-sky-700 border-sky-200',
  performance_metric: 'bg-violet-100 text-violet-700 border-violet-200',
  aesthetic_quality: 'bg-pink-100 text-pink-700 border-pink-200',
  lifestyle_signal: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  value_proposition: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pain_point: 'bg-rose-100 text-rose-700 border-rose-200',
  use_case: 'bg-amber-100 text-amber-700 border-amber-200',
  safety_concern: 'bg-red-100 text-red-700 border-red-200',
  sustainability: 'bg-teal-100 text-teal-700 border-teal-200',
  brand_perception: 'bg-slate-100 text-slate-700 border-slate-200',
};

export function ConceptTypeTree({ roots, selectedId, onSelect }: ConceptTypeTreeProps) {
  if (roots.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <p className="text-sm text-slate-400">No ConceptType canonicals match the filters.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-0.5 max-h-[70vh] overflow-auto">
      {roots.map((n) => (
        <ConceptTypeRow
          key={n.id}
          node={n}
          level={0}
          selectedId={selectedId}
          onSelect={onSelect}
          defaultExpanded={true}
        />
      ))}
    </div>
  );
}

function ConceptTypeRow({
  node,
  level,
  selectedId,
  onSelect,
  defaultExpanded,
}: {
  node: ConceptTypeNode;
  level: number;
  selectedId: string | null;
  onSelect: (node: ConceptTypeNode) => void;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded && level < 2);
  const hasChildren = node.children.length > 0;
  const data = (node.canonical_data ?? {}) as Record<string, unknown>;
  const category = typeof data.type_category === 'string' ? data.type_category : null;
  const displayName = typeof data.display_name === 'string' ? data.display_name : null;
  const instanceCount =
    typeof data.instance_count === 'number' ? (data.instance_count as number) : 0;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors',
          isSelected
            ? 'bg-brand-teal/10 border border-brand-teal/30'
            : 'hover:bg-slate-50 border border-transparent',
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(node)}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="text-slate-400 hover:text-slate-700"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-3.5" />
        )}
        <span className="text-sm text-slate-700 truncate flex-1 min-w-0">
          {displayName || node.canonical_name}
        </span>
        {category && (
          <span
            className={clsx(
              'text-[10px] font-medium px-1.5 py-0.5 rounded-full border',
              CATEGORY_COLORS[category] ?? 'bg-slate-50 text-slate-600 border-slate-200',
            )}
          >
            {category.replace(/_/g, ' ')}
          </span>
        )}
        <span className="text-[10px] font-mono text-slate-400 flex-shrink-0">
          {instanceCount} · {node.children.length}ch
        </span>
      </div>
      {expanded && hasChildren && (
        <div>
          {node.children.map((c) => (
            <ConceptTypeRow
              key={c.id}
              node={c}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
